import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Card, Modal, Input, message } from 'antd';
import { SwapOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { useSearchPendingByMobileQuery, useUserDetailDataQuery } from '../../../services/user';
import usePermissions from '../../../hooks/usePermissions';
import './styles.scss';

/* ─── Types ──────────────────────────────────────────────────────────── */

interface CurrentMembership {
  isBalanceClear?: boolean;
}

interface UserData {
  hasMembership?: boolean;
  status?: string;
  isAdvanceRenewal?: boolean;
  isUpgrade?: boolean;
  totalDueAmount?: number;
  currentMembership?: CurrentMembership;
  member?: { canTransfer?: boolean };
}

interface UserOutletContext {
  userData?: UserData;
}

// Pull a field from an object trying several possible key names — the
// search-by-mobile endpoint's exact response shape isn't pinned down yet.
const pick = (o: any, ...keys: string[]) => {
  for (const k of keys) if (o && o[k] !== undefined && o[k] !== null && o[k] !== '') return o[k];
  return undefined;
};

/* ─── Component ──────────────────────────────────────────────────────── */

const BuyMembership = () => {
  const navigate          = useNavigate();
  const { id }            = useParams<{ id: string }>();
  const { userData }      = useOutletContext<UserOutletContext>();
  const { hasPermission } = usePermissions();
  const branchId          = useSelector((state: any) => state.branch.selectedBranch);
  const selectedBranchId  = typeof branchId === 'object' ? (branchId?._id || branchId?.id) : branchId;

  /* ── Membership Transfer: search target member by mobile, then hand off to Buy Plan for them ── */
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [mobileInput,       setMobileInput]        = useState('');
  const [searchMobile,      setSearchMobile]       = useState('');

  const { data: searchResp, isFetching: searching } = useSearchPendingByMobileQuery(
    { number: searchMobile, branchId: selectedBranchId || undefined },
    { skip: !searchMobile }
  );
  // RTK Query keeps the last successful `data` around even once the query is
  // skipped, so gate on `searchMobile` too — otherwise reopening the modal
  // (which clears searchMobile) still shows the previous search's result.
  const matchedUser = searchMobile
    ? ((searchResp as any)?.data ?? (searchResp as any)?.users ?? (Array.isArray(searchResp) ? searchResp : [searchResp]))[0]
    : undefined;

  // Fallback enrichment in case the pending-search response is missing a field —
  // pulls the full profile (age, gender, state, address, sales person) once we know the id.
  const { data: matchedDetailResp, isFetching: loadingDetail } = useUserDetailDataQuery(
    matchedUser?._id,
    { skip: !matchedUser?._id }
  );
  const matchedDetail = matchedUser?._id ? (matchedDetailResp as any)?.user : undefined;

  const canTransfer = userData?.member?.canTransfer === true && hasPermission('9-10-membership-transfer');

  const openTransferModal = () => {
    setMobileInput('');
    setSearchMobile('');
    setTransferModalOpen(true);
  };

  const closeTransferModal = () => {
    setTransferModalOpen(false);
    setMobileInput('');
    setSearchMobile('');
  };

  const handleSearchMobile = () => {
    if (mobileInput.trim().length < 10) { message.warning('Enter a valid mobile number'); return; }
    setSearchMobile(mobileInput.trim());
  };

  const handleContinueTransfer = () => {
    if (!matchedUser?._id || !id) return;
    closeTransferModal();
    const params = new URLSearchParams({ membershipTransfer: 'true', fromUserId: id });
    navigate(`/user-detail/${matchedUser._id}/buy-plan?${params.toString()}`);
  };

  if (!userData?.hasMembership || userData?.status === 'inactive') {
    return (
      <div className="buy-membership-container">
        <Card className="membership-card">
          <h2>Buy Membership</h2>
          <p className="description">Purchase your first membership to get started</p>
          <div className="action-buttons single">
            <Button
              type="primary"
              size="large"
              className="buy-plan-btn"
              onClick={() => navigate(`/user-detail/${id}/buy-plan`)}
            >
              {userData?.status === 'inactive' ? 'Renew Membership' : 'Buy Membership'}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const handleUpgrade      = () => navigate(`/user-detail/${id}/upgrade-plan`);
  const handleAdvanceRenew = () => navigate(`/user-detail/${id}/advance-renew`);
  const handlePayDueAmount = () => navigate(`/user-detail/${id}/pay-due-amount`);

  const hasDueAmount   = userData?.currentMembership?.isBalanceClear === true && (userData?.totalDueAmount ?? 0) > 0;
  const isAdvanceRenewal = userData?.isAdvanceRenewal === true;
  const isUpgrade        = userData?.isUpgrade === true;
  const hasNoActions     = !isUpgrade && !isAdvanceRenewal && !hasDueAmount && !canTransfer;

  return (
    <div className="buy-membership-container">
      <Card className="membership-card">
        {!hasNoActions && (
          <>
            <h2>Select Membership Action</h2>
            <p className="description">Choose an option to manage your membership</p>
          </>
        )}

        {hasNoActions ? (
          <div className="no-actions-message">
            <div className="icon">✓</div>
            <h3>All Set!</h3>
            <p>Your membership is active and up to date. No actions are required at this time.</p>
            <p className="sub-text">You'll be notified when renewal or upgrade options become available.</p>
          </div>
        ) : (
          <div className="action-buttons">
            {isUpgrade && (
              <Button type="primary" size="large" className="upgrade-btn" onClick={handleUpgrade}>
                Upgrade
              </Button>
            )}

            {isAdvanceRenewal && (
              <Button type="primary" size="large" className="renew-btn" onClick={handleAdvanceRenew}>
                Advance Renew
              </Button>
            )}

            {hasDueAmount && (
              <Button type="primary" size="large" className="pay-due-btn" onClick={handlePayDueAmount} danger>
                Pay Due Amount (₹{(userData?.totalDueAmount ?? 0).toLocaleString()})
              </Button>
            )}

            {canTransfer && (
              <Button size="large" className="transfer-btn" icon={<SwapOutlined />} danger ghost onClick={openTransferModal}>
                Membership Transfer
              </Button>
            )}
          </div>
        )}
      </Card>

      <Modal
        title="Membership Transfer"
        open={transferModalOpen}
        onCancel={closeTransferModal}
        footer={null}
        width={560}
        destroyOnClose
      >
        <div className="transfer-search-row">
          <Input
            placeholder="Enter mobile number"
            value={mobileInput}
            onChange={(e) => setMobileInput(e.target.value)}
            onPressEnter={handleSearchMobile}
            maxLength={15}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearchMobile} loading={searching}>
            Search
          </Button>
        </div>

        {searchMobile && !searching && !matchedUser && (
          <p className="transfer-no-match">No member found with this mobile number.</p>
        )}

        {matchedUser && (() => {
          const isPending = matchedUser.isLead === true || matchedUser.status === 'pending';

          const fields = [
            { label: 'Mobile No.',      wide: false, value: pick(matchedUser, 'mobileNumber', 'phoneNumber') ?? pick(matchedDetail, 'phoneNumber') },
            { label: 'Customer Name',   wide: false, value: pick(matchedUser, 'name') ?? pick(matchedDetail, 'name') },
            { label: 'Age',             wide: false, value: pick(matchedUser, 'age') ?? pick(matchedUser?.member, 'age') ?? pick(matchedDetail?.member, 'age') },
            { label: 'Gender',          wide: false, value: pick(matchedUser, 'gender') ?? pick(matchedUser?.member, 'gender') ?? pick(matchedDetail?.member, 'gender') },
            { label: 'State Name',      wide: false, value: pick(matchedUser, 'stateName', 'state') ?? pick(matchedUser?.member, 'stateName', 'state') ?? pick(matchedDetail?.member, 'stateName', 'state') },
            { label: 'Billing Address', wide: true,  value: pick(matchedUser, 'address', 'billingAddress') ?? pick(matchedUser?.member, 'address', 'billingAddress') ?? pick(matchedDetail?.member, 'address', 'billingAddress') },
            { label: 'Sales Person',    wide: false, value: pick(matchedUser, 'salesPerson') ?? pick(matchedUser?.salesPerson, 'name') ?? pick(matchedDetail?.salesPerson, 'name') },
            { label: 'Branch Name',     wide: false, value: pick(matchedUser, 'branch', 'branchName') ?? pick(matchedUser?.branchIds?.[0], 'name') ?? pick(matchedDetail?.branchIds?.[0], 'name') },
          ].filter(f => f.value !== undefined);

          return (
            <>
              {loadingDetail && !matchedDetail && <p className="transfer-loading">Loading member details…</p>}

              {isPending && <div className="transfer-pending-tag">Pending Lead — no member profile yet</div>}

              <div className="transfer-details-grid">
                {fields.map(f => (
                  <div key={f.label} className={`transfer-field ${f.wide ? 'wide' : ''}`}>
                    <span className="label">{f.label}</span>
                    <span className="value">{f.value}</span>
                  </div>
                ))}
              </div>

              <div className="transfer-continue-row">
                <Button type="primary" onClick={handleContinueTransfer}>
                  Continue
                </Button>
              </div>
            </>
          );
        })()}
      </Modal>
    </div>
  );
};

export default BuyMembership;
